<template>
  <div>
    <span style="display: none">{{ status }}</span>
    <c-row class="mb-3">
      <c-col>
        <c-button class="btn btn-primary float-left" size="sm" @click="get(1)">
          <c-spinner v-if="isLoading" color="success" size="sm" />
          <span v-else>Refresh</span>
        </c-button>
        <c-dropdown
            color="primary"
            size="sm"
            toggler-text="Run Backup"
            class="btn-primary float-right"
          >
          <c-dropdown-item @click="confirmRunBackup('full')">
            <c-spinner v-if="isRunningBackup" color="success" size="sm" />
            <span v-else>Full</span>
          </c-dropdown-item>
          <c-dropdown-item @click="confirmRunBackup('database')">
            <c-spinner v-if="isRunningBackup" color="success" size="sm" />
            <span v-else>Only Database</span>
          </c-dropdown-item>
          <c-dropdown-item @click="confirmRunBackup('files')">
            <c-spinner v-if="isRunningBackup" color="success" size="sm" />
            <span v-else>Only Files</span>
          </c-dropdown-item>
        </c-dropdown>
      </c-col>
    </c-row>
    <template v-if="!isLoading && !backups.length">
      <p class="lead">No site backups have been run.</p>
    </template>
    <template v-else>
      <table v-if="backups.length" class="table">
        <thead>
          <tr>
            <th style="width: 200px">Date</th>
            <th>Type</th>
            <th>Status</th>
            <th>Info</th>
            <th />
          </tr>
        </thead>
        <tbody>
          <tr v-for="backup in backups">
            <td>{{ backup.createdAt | dateFormatWithTime }}</td>
            <td>{{ backup.backupType }}</td>
            <td>{{ backup.status }}</td>
            <td>
              <template v-if="backup.status === 'completed'">
                  <!--Show nothing -->
              </template>
              <template v-if="backup.status === 'running'">
                {{ backup.info }}
                <c-spinner color="success" size="sm" />
              </template>
              <template v-else>
                <!--              Anything to go here? -->
              </template>
            </td>
            <td>
              <c-dropdown
                  v-if="backup.status === 'completed'"
                  color="primary"
                  size="sm"
                  toggler-text="Small"
                  class="d-inline-block float-right"
                  :caret="false"
              >
                <template #toggler-content>
                  <c-icon :content="freeSet.cilSettings" />
                  <span class="sr-only">Search</span>
                </template>
                <c-dropdown-item>
                  <c-spinner v-if="backup.isDownloading" color="success" size="sm" />
                  <span v-else @click="download(backup)">Download</span>
                </c-dropdown-item>
                <c-dropdown-item v-if="backup.backupType === 'full'" @click="restoreBackup(backup)">
                  Clone
                </c-dropdown-item>
              </c-dropdown>
            </td>
          </tr>
        </tbody>
      </table>
    </template>
    <CloneSite :site="site" :backup="selectedBackup" :show="showRestoreBackup" @close="showRestoreBackup = !showRestoreBackup"/>
    <confirm-modal
        :title="`Backup site: ${site.name}`"
        ok-text="Run"
        :show.sync="showRunBackup"
        @update:confirm="runBackup(backupType)"
    >
      <template #footer>
        <button :disabled="isRunningBackup" type="button" class="btn btn-secondary" @click="showRunBackup = false">
          Cancel
        </button>
        <button :disabled="isRunningBackup" type="button" class="btn btn-primary" @click="runBackup(backupType)">
          <c-spinner v-if="isRunningBackup" color="success" size="sm" />
          <span v-else>Run</span>
        </button>
      </template>
      <p v-html="backupMessage"/>
    </confirm-modal>
  </div>
</template>

<script>

import io from 'socket.io-client';
import Vue from 'vue';
import { freeSet } from '@coreui/icons';
import { buildEndpoint } from '@/libs/buildEndPoint';
import CloneSite from './clone';
import ConfirmModal from '../ConfirmModal';

const socket = io(buildEndpoint(''));

export default {
  name: 'BackupsList',
  components: {
    CloneSite,
    ConfirmModal,
  },
  props: {
    siteId: String,
    site: Object,
  },
  data() {
    return {
      freeSet,
      showRunBackup: false,
      isLoading: false,
      isRunningBackup: false,
      pages: {},
      backups: [],
      status: '',
      showRestoreBackup: false,
      selectedBackup: null,
      backupType: null,
    };
  },
  computed: {
    backupMessage() {
      const messages = {
        full: 'Run a full backup of this site?',
        database: 'Only backup the database of this site?',
        files: 'Only backup the files of this site?',
      };
      return messages[this.backupType];
    },
  },
  mounted() {
    this.get();
  },
  created() {
    const _this = this;
    socket.emit('room', Vue.localStorage.get('mp-sid'));
    socket.on('siteBackupNotification', (data) => {
      // Update backup status of site
      // eslint-disable-next-line no-param-reassign,no-return-assign,no-underscore-dangle
      const backup = _this.backups.find((b) => b._id === data.backupId);
      if (backup) {
        backup.status = data.status;
        if (['database', 'files'].includes(data.backupType)) {
          // backup.percentageBackedUp = data.percentageBackedUp;
          backup.info = `Backing up: ${(data.percentageBackedUp * 100).toFixed(2)}%`;
        } else {
          // backup.percentageBackedUp = data.percentageBackedUp;
          backup.info = `Database: ${((data.percentageBackedUpDatabase || 0) * 100).toFixed(2)}%`;
          backup.info += ` · Files: ${((data.percentageBackedUpFiles || 0) * 100).toFixed(2)}%`;
        }
        // @TODO - this is a complete bodge to force the data in the array to be reactive.
        // @TODO - find a better way to do this. Seriously. There is a hidden element above the table, if this is
        // @TODO - not there, the array data will not update in the table.
        _this.status = backup.info;
      }
    });
  },
  methods: {
    confirmRunBackup(type) {
      this.backupType = type;
      this.showRunBackup = true;
    },
    runBackup(type) {
      const endpoint = buildEndpoint(type === 'full' ? `site/backups/full/${this.siteId}` : `site/backups/run/${type}/${this.siteId}`);
      this.isRunningBackup = true;
      this.$http
        .get(endpoint)
        .then((response) => {
          if (response.data.success) {
            this.get();
            this.$store.commit('addSuccessMessage', {
              header: 'Backup',
              message: 'Your backup has been requested and will begin shortly.',
            });
          } else {
            this.error = 'Error running backup';
            this.$store.commit('addErrorMessage', {
              header: 'Error starting backup',
              message: response.data.error,
            });
          }
        })
        .catch((error) => {
          this.error = error;
          this.$store.commit('addErrorMessage', {
            header: 'Error starting backup',
            message: error,
          });
        }).finally(() => {
          this.isRunningBackup = false;
          this.showRunBackup = false;
        });
    },
    get(pageNum) {
      this.isLoading = true;
      this.error = null;
      const { _id: siteId } = this.site;
      this.$http
        .get(`${buildEndpoint('site/backups/list/') + siteId}?page=${pageNum || 1}`)
        .then((response) => {
          this.isLoading = false;
          if (response.data.success) {
            this.backups = response.data.data.map((b) => {
              b.isDownloading = false;
              return b;
            });
            // const data = response.data.data
            // this.pluginUpdateLogs = data.docs
            // let pageList = []
            // for (let i = 1; i <= data.pages; i++) {
            //   if (i > data.page - 5 && i < data.page + 5) {
            //     pageList.push(i)
            //   }
            // }
            // this.pages = {
            //   numPages: data.pages,
            //   currentPage: data.page,
            //   totalDocs: data.total,
            //   pageList: pageList
            // }
          } else {
            this.error = 'Error getting backups';
          }
        })
        .catch((error) => {
          this.error = error;
        });
    },
    download(file) {
      const { _id: fileId } = file;
      // eslint-disable-next-line no-underscore-dangle
      const fileToDownload = this.backups.find((backup) => backup._id === fileId);
      fileToDownload.isDownloading = true;
      this.$http
        .get(buildEndpoint(`site/backups/request-zip/${fileId}`))
        .then((response) => {
          if (response.data.success) {
            this.$store.commit('addSuccessMessage', {
              header: 'Your download has been requested.',
              message: "We'll send you an email when it's ready!",
            });
          } else {
            this.$store.commit('addErrorMessage', {
              header: 'There was an error downloading the file',
              message: response.data.error || '',
            });
          }
        })
        .catch(() => {
          this.$store.commit('addErrorMessage', {
            header: 'There was an error downloading the file',
          });
        }).finally(() => {
          fileToDownload.isDownloading = false;
        });
    },
    restoreBackup(backup) {
      this.showRestoreBackup = true;
      this.selectedBackup = backup;
    },
  },
};
</script>
